import {useContext, useEffect, useState, useRef} from 'react';

import AsyncSelect from 'react-select/async';
import classes from '../Auth/AuthForm.module.css';
import './PatientSelectionForm.css'
import AuthContext from "../../store/auth-context";
import PatientContext from "../../store/patient-context";
import {
  getPatientList,
} from "../../common/BackendConnections";
import {
  patientRole,
  limit,
  CapitalizeFirstLetter,
  notNull,
  hasUsername,
  AddUser
} from "../../common/UserManagement"
import {Link} from "react-router-dom";
import UserActionDialog from "../Admin/UserActionDialog";
import nullUserInfo from "../Admin/UserInfo";
import {ArrowBarDown} from "react-bootstrap-icons";

const getCuratedPatientList = (searchString = '', printErrorMessage) => {
  return getPatientList(searchString, 0, limit).then((response) => {
      const patient_data = response.data.user_list.map((patient) => (
        {value: patient.user_id, label: `${patient.given_name} ${patient.family_name}`}
      ))
      if (patient_data.length === limit) {
        patient_data.push({value: 0, label: '-- More data available -- Type to search'});
      }
      return patient_data;
    }
  ).catch((error) => {
    console.error(error);
    printErrorMessage(error.message);
  });
}

const createDefaultPatientList = (setDefaultPatients, hasMoreData, printErrorMessage) => {
  getCuratedPatientList('', printErrorMessage).then((response) => {
    if (response.length >= limit) {
      hasMoreData.current = true;
    }
    setDefaultPatients(response);
  })
}

const PatientSelectionForm = (props) => {
  const [defaultPatients, setDefaultPatients] = useState(null);
  const hasMoreData = useRef(false);
  const getOptionsTimer = useRef();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [message, setMessage] = useState("");
  const messageTimer = useRef();
  const [addingUserInfo, setAddingUserInfo] = useState(nullUserInfo);

  const printErrorMessage = (message) => {
    const messageDisplayTime = 5000 // ms
    clearTimeout(messageTimer.current)
    setMessage(message);
    messageTimer.current = setTimeout(() => {setMessage("");}, messageDisplayTime);
  }

  useEffect(() => {
    createDefaultPatientList(setDefaultPatients, hasMoreData, printErrorMessage);
  }, []);

  const localOptionFilter = (inputValue) => {
    return defaultPatients.filter((i) =>
      i.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  const getOptionsTimerWait = 400;  // Number of ms to wait after the user type the last char in the search field
  const getOptions = (searchString = '') => {
    // In here, getOptionsTimer is used to debounce the option search.
    // Every this a character is typed, the timer (and thus the delayed function call) is cleared.
    // Hence, the actual search is only done after a pause of 'getOptionsTimerWait' ms in user entry.
    clearTimeout(getOptionsTimer.current);
    if (hasMoreData.current) {
      // The frontend only has a partial list: we ask the backend to handle the search
      return new Promise((resolve) => {
        getOptionsTimer.current = setTimeout(() => {
        resolve(getCuratedPatientList(searchString, printErrorMessage));
        }, getOptionsTimerWait);
      });
    } else {
      // The frontend has all data: we do a local search
      return new Promise((resolve) => {
        getOptionsTimer.current = setTimeout(() => {
          resolve(localOptionFilter(searchString));
        }, getOptionsTimerWait);
      });
    }
  }

  const submitHandler = (event) => {
    event.preventDefault();
    if (! selectedPatient) {
      printErrorMessage('Please select a patient first')
    } else if (selectedPatient.value === 0) {
      printErrorMessage('Invalid selection')
    } else {
      props.onSelection(selectedPatient.value);
      patientCtx.update(selectedPatient.value, selectedPatient.label)
    }
  }

  const getAddUserCallback = (userRole) => {
    return (onSuccessCallback) => {
      const _userInfo = {...nullUserInfo, userType: userRole, isActive: true, onSuccessCallback: onSuccessCallback};
      setAddingUserInfo(_userInfo);
    }
  }

  const onAddUser = () => {
    const callback = getAddUserCallback(patientRole);
    callback(() => {})
  }

  const authCtx = useContext(AuthContext);
  const patientCtx = useContext(PatientContext);

  return (
    <section className={"page_content"}>
      <div className={"patient-selection-page"}>

        <h1 style={{fontWeight:'bold', marginBottom: '.5em'}}>Welcome {authCtx.userFullName}</h1>

        <h5>Select a Patient</h5>

        <form onSubmit={submitHandler} style={{marginTop: '3em'}}>

          <div className={classes.control}>
            <div className={"select-style"}>
              {defaultPatients === null ? (
                <AsyncSelect defaultOptions
                             placeholder={"- Select Patient or Start typing -"}
                />) : (
                <AsyncSelect defaultOptions={defaultPatients}
                             loadOptions={(inputValue) => {return getOptions(inputValue);}}
                             placeholder={"- Select Patient or Start typing -"}
                             onChange={(x) => setSelectedPatient(x)}
                />)
              }
            </div>
          </div>

          <div className={classes.actions}>
            <button className={"text-capitalize"}>Submit</button>
            {message && <div className={"error_message"}>{message}</div>}
            <div className={"text-start"} style={{width: '100%'}}>
              Can't find Patient's ID?
              <Link to={"/help/"} style={{marginLeft: '.5em'}}>Get Help</Link> <br/>
            </div>
          </div>

        </form>

        <div style={{marginTop: "12rem", marginBottom: "2rem"}}>
          <hr style={{marginTop: "0rem", marginBottom: "0rem"}}/>
          <h5 style={{marginTop: "0rem"}}>or</h5>
          <hr style={{marginTop: "0rem", marginBottom: "0rem"}}/>
        </div>

        <div className={classes.actions}>
          <button id="addButton" className={"sessionAdd-Button"} onClick={onAddUser}>
            {"Add a New Patient"}
          </button>
        </div>

      </div>

      {/*Modal dialog for adding a new user - here a patient*/}
      { notNull(addingUserInfo) &&
          <UserActionDialog windowTitle={"Add " + CapitalizeFirstLetter(addingUserInfo.userType) + " User"}
                            show={true}
                            userType={addingUserInfo.userType}
                            hasEmail={true}
                            hasUsername={hasUsername(addingUserInfo)}
                            defaultUserInfo={addingUserInfo}
                            handleFormExit={() => {
                                setAddingUserInfo(nullUserInfo)
                                createDefaultPatientList(setDefaultPatients, hasMoreData, printErrorMessage)
                              }
                            }
                            processFormData={AddUser} />
      }

    </section>
  );
};

export default PatientSelectionForm;
